import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";
import { Loader2 } from "lucide-react";
import "leaflet/dist/leaflet.css";

const COLORS = {
    historical: "#22c55e",
    user: "#3b82f6",
    fallback: "#a855f7",
};

export default function WorldMap() {
    const { theme } = useTheme();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entityFilter, setEntityFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [minCred, setMinCred] = useState(0);

    useEffect(() => {
        api.get("/map/encounters", { params: { limit: 2000 } })
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    }, []);

    const entities = useMemo(() => Array.from(new Set(items.map((i) => i.entity_known_type).filter(Boolean))).sort(), [items]);
    const types = useMemo(() => Array.from(new Set(items.map((i) => i.encounter_type).filter(Boolean))).sort(), [items]);

    const filtered = items.filter((i) =>
        (!entityFilter || i.entity_known_type === entityFilter) &&
        (!typeFilter || i.encounter_type === typeFilter) &&
        (i.credibility_score || 0) >= minCred);

    const tileUrl = theme === "dark"
        ? "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
        : "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png";

    return (
        <div className="ok-container py-8" data-testid="world-map-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">Atlas</div>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">World Encounter Map</h1>
                    <p className="opacity-60 font-mono text-xs uppercase tracking-widest mt-2">{filtered.length} pinned encounters</p>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                    <FilterSel label="Entity" value={entityFilter} onChange={setEntityFilter} options={entities} testId="map-filter-entity" />
                    <FilterSel label="Type" value={typeFilter} onChange={setTypeFilter} options={types} testId="map-filter-type" />
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-1.5">Min Evidence Index: {minCred}</div>
                        <input type="range" min="0" max="100" step="10" value={minCred} onChange={(e) => setMinCred(parseInt(e.target.value))} className="w-40" data-testid="map-filter-credibility" />
                    </div>
                </div>
            </div>

            <div className="ok-card overflow-hidden" data-testid="leaflet-container" style={{ height: "70vh" }}>
                {loading ? (
                    <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : (
                    <MapContainer center={[20, 0]} zoom={2} minZoom={2} maxZoom={10} style={{ height: "100%", background: "var(--ok-bg)" }} worldCopyJump>
                        <TileLayer
                            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                            url={tileUrl}
                        />
                        {filtered.map((e) => {
                            const c = COLORS[e.source_type] || COLORS.fallback;
                            const r = Math.max(5, Math.min(14, (e.credibility_score || 50) / 8));
                            return (
                                <CircleMarker
                                    key={e.id}
                                    center={[e.lat, e.lng]}
                                    pathOptions={{ color: c, weight: 1.5, fillColor: c, fillOpacity: 0.6 }}
                                    radius={r}
                                >
                                    <Popup>
                                        <div className="min-w-[220px]" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
                                            <div className="font-bold text-sm mb-1">{e.title}</div>
                                            <div className="text-[11px] opacity-70 mb-1">
                                                {(e.encounter_date || "").slice(0, 10) || "Unknown date"} · {e.location || e.country}
                                            </div>
                                            <div className="text-[11px] opacity-70 mb-2">
                                                Evidence Index: <strong>{e.credibility_score ?? "?"}/100</strong>
                                                {e.entity_known_type && <> · {e.entity_known_type}</>}
                                            </div>
                                            <Link to={`/encounters/${e.id}`} className="text-[11px] underline font-bold" style={{ color: "#22c55e" }}>
                                                Open case →
                                            </Link>
                                        </div>
                                    </Popup>
                                </CircleMarker>
                            );
                        })}
                    </MapContainer>
                )}
            </div>

            <div className="flex items-center gap-4 mt-3 text-xs opacity-70 font-mono uppercase tracking-widest">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.historical }} /> Historical</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.user }} /> User submitted</span>
            </div>
        </div>
    );
}

function FilterSel({ label, value, onChange, options, testId }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-1.5">{label}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="ok-input text-sm w-40" data-testid={testId}>
                <option value="">All</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
